import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/contexts/AuthContext";
import type { ModuleKey } from "@/modules/registry";

interface ModuleGuardProps {
    required: ModuleKey | ModuleKey[];
    children: ReactNode;
}

export function ModuleGuard({ required, children }: ModuleGuardProps) {
    const { user, loading: isLoadingAuth } = useAuth();
    const { data: profile, isLoading: isLoadingProfile } = useProfile();
    const { data, isLoading: isLoadingEntitlements, isError: isEntitlementsError } = useEntitlements();

    // Wait for auth + profile + entitlements before making any decisions
    const isProfilePending = isLoadingProfile || profile === undefined;
    const isEntitlementsPending =
        !isEntitlementsError &&
        (isLoadingEntitlements || (!isProfilePending && !!profile?.current_clinic_id && data === undefined));

    if (isLoadingAuth || !user || isProfilePending || isEntitlementsPending) {
        return null;
    }

    const active = new Set(data?.activeModules || []);

    // Fallback rule: if no modules resolved, allow core surface (home/customers/calendar)
    const requiredList = Array.isArray(required) ? required : [required];
    if (requiredList.includes("core") && (active.size === 0 || active.has("core"))) {
        return <>{children}</>;
    }
    if (requiredList.some((module) => active.has(module))) {
        return <>{children}</>;
    }

    return <Navigate to="/calendar" replace />;
}
